import * as React from 'react';

import {
  ItemGroupSortOption,
  enumItemGroupSortOption,
} from '@rematter/frontend-lib-api-client';
import { ItemGroupRecord } from '@rematter/modules/settings/types/inventoryItem';
import {
  Button,
  DataGrid,
  ListScreenLayout,
  Pagination,
  SearchField,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import * as state from '../../../_state/inventory.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { useItemGroupsPanelColumns } from './hooks/useItemGroupsColumns';
import { ItemGroupCreateDialog } from './ItemGroupCreateDialog';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: ItemGroupSortOption; desc: ItemGroupSortOption }
> = {
  name: {
    asc: enumItemGroupSortOption.NameAsc,
    desc: enumItemGroupSortOption.NameDesc,
  },
  number: {
    asc: enumItemGroupSortOption.NumberAsc,
    desc: enumItemGroupSortOption.NumberDesc,
  },
  createdAt: {
    asc: enumItemGroupSortOption.CreatedAtAsc,
    desc: enumItemGroupSortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ ITEM GROUPS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const ItemGroupsListView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteItemGroups,
  );
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);
  const [selectedKeys, setSelectedKeys] = useAtom(state.$groupsSelectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isGroupCreateDialogOpen,
  );
  const setCurrentSorting = useSetAtom(state.$groupsSorting);

  // Callbacks
  const handleRowClick = React.useCallback(
    (itemGroup: ItemGroupRecord) => {
      setLocation(`/inventory/item-groups/${itemGroup.id}`);
    },
    [setLocation],
  );
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumItemGroupSortOption.NameAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleCreateItemGroup = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  // Other data hooks
  const { columns, columnVisibilityState } = useItemGroupsPanelColumns({
    onRowClick: handleRowClick,
  });

  const itemGroups = data?.itemGroups ?? [];
  const total = data?.totalCount ?? 0;

  const emptyPanelProps = {
    title: t.inventoryView.itemGroupsPanel.emptyState.title,
    description: (
      <span>{t.inventoryView.itemGroupsPanel.emptyState.description}</span>
    ),
    action: (
      <Button
        onPress={() => setIsCreateDialogOpen(true)}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.inventoryView.itemGroupsPanel.actions.create}
      </Button>
    ),
    centered: true,
  };

  return (
    <ListScreenLayout
      filters={
        <SearchField
          autoFocus
          value={searchTerm ?? ''}
          onChange={(value) => {
            setSearchTerm(value || undefined);
            setOffset(0);
          }}
          placeholder={t.inventoryView.itemGroupsPanel.search.placeholder}
          aria-label={t.inventoryView.itemGroupsPanel.search.ariaLabel}
          className="w-[200px] shrink-0"
        />
      }
      actions={
        <Button
          onPress={handleCreateItemGroup}
          variant="solid"
          color="primary"
          icon={<LucidePlusCircle />}
        >
          {t.inventoryView.itemGroupsPanel.actions.create}
        </Button>
      }
      footer={
        total > limit ? (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        ) : undefined
      }
      className="@ItemGroupsListView"
    >
      <DataGrid<ItemGroupRecord>
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        dataSource={itemGroups}
        rowKey="id"
        aria-label={t.inventoryView.itemGroupsPanel.ariaLabel}
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        onColumnSort={handleColumnSort}
        columnVisibilityState={columnVisibilityState}
        className="w-full text-sm"
        fullWidth
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description:
                  t.inventoryView.itemGroupsPanel.errors.loadingFailed(
                    error.message,
                  ),
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={emptyPanelProps}
      />
      <ItemGroupCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => refetch?.()}
      />
    </ListScreenLayout>
  );
};
